/**
 * FolderSheetList — every sheet in the open drawing's folder as one flat,
 * scrollable list (crossing file boundaries). Click a row to open that
 * sheet, pencil-rename any page (names persist in the job state), and run
 * Auto Name Folder to name every PDF in the folder from one title-block box.
 */
import { useState } from "react";
import { Bookmark, Check, ListOrdered, Pencil, X } from "lucide-react";
import { MONO } from "./theme";

export default function FolderSheetList({ folderName, sheets, activeKey, doc, onJump, onRename, onAutoNameFolder, onClose }) {
  const [editing, setEditing] = useState(null); // sheet key
  const [editName, setEditName] = useState("");

  const commit = (s) => {
    const nm = editName.trim();
    if (nm && nm !== s.name) onRename(s.drawing.drawing_id, s.page, nm);
    setEditing(null);
  };

  return (
    <div className="absolute inset-y-0 right-0 z-30 flex w-[340px] max-w-[85%] flex-col border-l border-[#24324a] bg-[#0b172a] shadow-2xl" data-testid="folder-sheet-list">
      <div className="flex items-center gap-2 border-b border-[#24324a] px-3 py-2.5">
        <ListOrdered className="h-4 w-4 shrink-0 text-[#f97316]" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-white" data-testid="folder-sheet-list-title">{folderName}</p>
          <p className="text-[10px] text-white/40">{sheets.length} sheet{sheets.length === 1 ? "" : "s"} in this folder</p>
        </div>
        {onAutoNameFolder && (
          <button
            onClick={onAutoNameFolder}
            className="shrink-0 rounded-md bg-[#f97316] px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white hover:bg-[#fb923c]"
            title="Draw a box around the sheet number on the open page — the same spot is read on every PDF in this folder"
            data-testid="auto-name-folder-btn"
          >
            Auto name folder
          </button>
        )}
        <button onClick={onClose} aria-label="Close folder sheet list" className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white" data-testid="folder-sheet-list-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {sheets.map((s, i) => {
          const active = s.key === activeKey;
          const ps = (doc?.pages || {})[s.key] || {};
          const count = (doc?.items || []).filter((it) => it.fileId === s.drawing.drawing_id && it.page === s.page).length;
          return (
            <div
              key={s.key}
              className={`group flex items-center gap-1.5 rounded-lg px-2 py-1.5 ${active ? "bg-[#f97316]/15 ring-1 ring-[#f97316]" : "hover:bg-white/5"}`}
              data-testid={`folder-sheet-row-${s.key}`}
            >
              <span className={`${MONO} w-7 shrink-0 text-right text-[10px] ${active ? "text-[#f97316]" : "text-white/35"}`}>{i + 1}</span>
              {editing === s.key ? (
                <>
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={() => commit(s)}
                    onKeyDown={(e) => { if (e.key === "Enter") commit(s); if (e.key === "Escape") setEditing(null); }}
                    className="h-6 min-w-0 flex-1 rounded border border-white/20 bg-white/10 px-1.5 text-xs text-white focus:border-[#f97316] focus:outline-none"
                    data-testid={`folder-sheet-rename-input-${s.key}`}
                  />
                  <button aria-label="Save sheet name" onClick={() => commit(s)} className="rounded p-1 text-emerald-400 hover:bg-white/10" data-testid={`folder-sheet-rename-save-${s.key}`}><Check className="h-3.5 w-3.5" /></button>
                </>
              ) : (
                <>
                  <button className="min-w-0 flex-1 truncate text-left text-xs text-white/85" onClick={() => onJump(i)} data-testid={`folder-sheet-open-${s.key}`} title={s.label}>
                    {s.label}
                  </button>
                  {ps.feetPerUnit ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" title={`Scale: ${ps.scaleLabel || "set"}`} /> : null}
                  {count > 0 && <span className="shrink-0 rounded bg-blue-500/20 px-1 text-[9px] font-bold text-blue-300" title={`${count} takeoffs`}>{count}</span>}
                  {ps.bookmarked && <Bookmark className="h-2.5 w-2.5 shrink-0 fill-[#f97316] text-[#f97316]" />}
                  <button
                    aria-label={`Rename ${s.label}`}
                    onClick={() => { setEditing(s.key); setEditName(s.name); }}
                    className="shrink-0 rounded p-1 text-white/25 opacity-0 transition-opacity hover:bg-white/10 hover:text-white group-hover:opacity-100"
                    data-testid={`folder-sheet-rename-${s.key}`}
                  ><Pencil className="h-3 w-3" /></button>
                </>
              )}
            </div>
          );
        })}
        {!sheets.length && <p className="px-3 py-6 text-center text-xs text-white/35">No viewable sheets in this folder.</p>}
      </div>
    </div>
  );
}
